/** HIGH and URGENT are red, LOW is quiet; any casing (a stub or an older payload) reads the same. */
export function priorityTone(priority) {
    const p = (priority ?? '').toUpperCase();
    if (p === 'URGENT' || p === 'HIGH')
        return 'danger';
    return 'neutral';
}
/** The status badge tone: waiting for the customer is a warning, finished is a success. */
export function ticketStatusTone(status) {
    if (status === 'PENDING')
        return 'warning';
    if (status === 'RESOLVED' || status === 'CLOSED')
        return 'success';
    return 'neutral';
}
/** How long a ticket has been open, in the largest whole unit (minutes, hours or days). */
export function ticketAge(createdAt, nowIso) {
    const minutes = Math.max(0, Math.floor((Date.parse(nowIso) - Date.parse(createdAt)) / 60_000));
    if (!Number.isFinite(minutes))
        return { unit: 'm', value: 0 };
    if (minutes < 60)
        return { unit: 'm', value: minutes };
    if (minutes < 60 * 24)
        return { unit: 'h', value: Math.floor(minutes / 60) };
    return { unit: 'd', value: Math.floor(minutes / (60 * 24)) };
}
/** Who works a ticket, from the caller's point of view (the queue never names another agent). */
export function assigneeOf(row) {
    if (row.assignedToMe)
        return 'me';
    return row.assignedAgent !== undefined ? 'other' : 'none';
}
const OPEN = ['OPEN', 'ASSIGNED', 'PENDING', 'REOPENED'];
export function deskPrimaryAction(ticket) {
    const open = OPEN.includes(ticket.status);
    if (open && !ticket.assignedToMe)
        return ticket.status === 'ASSIGNED' ? undefined : 'assign';
    if (ticket.assignedToMe && ticket.status === 'PENDING')
        return 'resume';
    if (ticket.assignedToMe && (ticket.status === 'ASSIGNED' || ticket.status === 'REOPENED'))
        return 'resolve';
    if (ticket.assignedToMe && ticket.status === 'RESOLVED')
        return 'close';
    return undefined;
}
/** Whether the secondary work actions (wait, link a ride, notes-driven work, compensation) apply: mine and open. */
export function canWork(ticket) {
    return ticket.assignedToMe && OPEN.includes(ticket.status);
}
/**
 * Why a finished ticket is reopened (D384 review, W5S-05) — the reason code the existing `REOPEN` action records on the
 * case audit. A closed vocabulary, so the audit reads the same whatever the agent's language.
 */
export const TICKET_REOPEN_REASONS = ['CUSTOMER_CAME_BACK', 'NOT_RESOLVED', 'CLOSED_IN_ERROR'];
/** Whether the detail offers Reopen: the ticket is finished (resolved or closed) — anyone who works tickets may reopen. */
export function canReopen(ticket) {
    return ticket.status === 'RESOLVED' || ticket.status === 'CLOSED';
}
export const QUEUE_STATUS_FILTERS = ['open', 'OPEN', 'ASSIGNED', 'PENDING', 'REOPENED', 'RESOLVED', 'CLOSED', 'all'];
/** The `?status=` the queue sends for a filter; undefined → the server's default (the open tickets). */
export function queueStatusParam(filter) {
    if (filter === 'open')
        return undefined;
    if (filter === 'all')
        return 'ALL';
    return filter;
}
/** A queue row's SLA as one badge, from the server's `slaState` (a row without one says so, never guesses). */
export function slaBadge(row, nowIso) {
    const s = row.slaState;
    if (s === undefined)
        return { tone: 'neutral', messageId: 'sla.unknown' };
    if (s.overdue)
        return { tone: 'danger', messageId: 'sla.breached' };
    if (s.nextDueAt === undefined)
        return { tone: 'success', messageId: 'sla.met' };
    const minutes = Math.max(0, Math.ceil((Date.parse(s.nextDueAt) - Date.parse(nowIso)) / 60_000));
    if (!Number.isFinite(minutes))
        return { tone: 'neutral', messageId: 'sla.unknown' };
    return { tone: minutes <= 60 ? 'warning' : 'neutral', messageId: 'sla.dueIn', minutes };
}
/** "due in" as a compact duration: minutes under two hours, then hours, then days. */
export function dueInParts(minutes) {
    if (minutes < 120)
        return { unit: 'm', value: minutes };
    if (minutes < 48 * 60)
        return { unit: 'h', value: Math.floor(minutes / 60) };
    return { unit: 'd', value: Math.floor(minutes / (24 * 60)) };
}
/** A short ticket reference for the queue (the full id is on the detail). */
export function shortTicketId(id) {
    return id.length > 8 ? `…${id.slice(-8)}` : id;
}
/**
 * D856 (owner D845 Q6): what a case is called on a screen - its human protocol number (`VEL-2026-000123`), or the short id for a case that
 * has none (opened before the protocol existed, or a stub).
 */
export function ticketReference(t) {
    return t.protocol !== undefined && t.protocol !== '' ? t.protocol : shortTicketId(t.ticketId);
}
/**
 * D856 (Q6): whether text typed in a search box reads as a protocol number - an optional short code, a four-digit year and a number,
 * with case, spaces, dashes and zero padding forgiven. Only a first look so the screen can say "not a protocol" without a round trip;
 * the server is the judge (`@vello/support` `parseProtocolQuery`).
 */
export function looksLikeProtocol(text) {
    return /^(?:[a-z0-9]{2,8}[\s-]+)?\d{4}[\s-]+\d{1,12}$/i.test(text.trim());
}
//# sourceMappingURL=index.js.map